/**
 * Whether Paige's answer to "next" (`POST /v1/jobs/{id}/review/next`, ADR-0009) still moves the
 * reviewer when it arrives. CapturePoint picks the next document in the renderer and moves at once,
 * and a Confirm whose save then fails brings the reviewer back to it. Paige's pick comes back from
 * the server a moment later, so two things can have happened meanwhile, and either one means the
 * answer is dropped: the reviewer opened another document themselves, or the Confirm behind the move
 * failed (the move would carry its error onto the next document, and the next Confirm would seal
 * that one; CapturePoint's own comment on confirmAndAdvance names the same failure).
 */
export function nextStillArrives(
  openNow: number,
  askedFrom: number,
  stillWanted?: () => boolean,
): boolean {
  return openNow === askedFrom && (stillWanted?.() ?? true);
}

/**
 * The documents Paige is asked to choose from for "next", in order: the screen's own pick first,
 * then the engine's teaching recommendation (CPv6 4bc6ae53), then the rest after the open document,
 * wrapping. CapturePoint jumps straight to the recommended document, which on a shared job would
 * put two reviewers on the same one; here the recommendation only reorders, and the server still
 * hands back the first one no colleague holds (ADR-0009).
 */
export function nextCandidates(
  ordered: readonly string[],
  teachingOrder: readonly string[],
  localPick?: string,
): string[] {
  const recommendedFirst = teachingOrder.length
    ? [
        ...teachingOrder.filter((id) => ordered.includes(id)),
        ...ordered.filter((id) => !teachingOrder.includes(id)),
      ]
    : [...ordered];
  const candidates = localPick
    ? [localPick, ...recommendedFirst.filter((id) => id !== localPick)]
    : recommendedFirst;
  return candidates.filter(Boolean);
}
